import { SelectedErrorColumns } from "./types";

// Pagination constants
export const OVERVIEW_PAGE_SIZE = 7;

interface ProductDefinition {
  id: string; // Product ID used in backend (e.g., "api", "async", "hosted")
  displayName: string; // UI display name (e.g., "API", "DataPipeline Projects")
  abbreviation: string; // Short display name (e.g., "api", "DP", "SDE-G")
  apiParam?: {
    sourceProduct?: string;
    structuredDataEndpoint?: string;
  };
}

const PRODUCTS: ProductDefinition[] = [
  {
    id: "api",
    displayName: "API",
    abbreviation: "api",
    apiParam: { sourceProduct: "api" },
  },
  {
    id: "async",
    displayName: "Async",
    abbreviation: "async",
    apiParam: { sourceProduct: "async" },
  },
  {
    id: "crawler",
    displayName: "Crawler",
    abbreviation: "crawler",
    apiParam: { sourceProduct: "crawler" },
  },
  {
    id: "hosted",
    displayName: "DataPipeline Projects",
    abbreviation: "DP",
    apiParam: { sourceProduct: "hosted" },
  },
  {
    id: "aiparser",
    displayName: "AI Parser",
    abbreviation: "AIP",
    apiParam: { sourceProduct: "aiparser" },
  },
  {
    id: "sde-google",
    displayName: "Structured Data Endpoint Google",
    abbreviation: "SDE-G",
    apiParam: { structuredDataEndpoint: "google" },
  },
  {
    id: "sde-amazon",
    displayName: "Structured Data Endpoint Amazon",
    abbreviation: "SDE-A",
    apiParam: { structuredDataEndpoint: "amazon" },
  },
  {
    id: "sde-walmart",
    displayName: "Structured Data Endpoint Walmart",
    abbreviation: "SDE-W",
    apiParam: { structuredDataEndpoint: "walmart" },
  },
  {
    id: "sde-ebay",
    displayName: "Structured Data Endpoint Ebay",
    abbreviation: "SDE-E",
    apiParam: { structuredDataEndpoint: "ebay" },
  },
  {
    id: "sde-redfin",
    displayName: "Structured Data Endpoint Redfin",
    abbreviation: "SDE-R",
    apiParam: { structuredDataEndpoint: "redfin" },
  },
  {
    id: "sde-homedepot-search",
    displayName: "Structured Data Endpoint Home Depot Search",
    abbreviation: "SDE-HDS",
    apiParam: { structuredDataEndpoint: "homedepotSearch" },
  },
  {
    id: "sde-homedepot-product",
    displayName: "Structured Data Endpoint Home Depot Product",
    abbreviation: "SDE-HDP",
    apiParam: { structuredDataEndpoint: "homedepotProduct" },
  },
  {
    id: "sde-homedepot-reviews",
    displayName: "Structured Data Endpoint Home Depot Reviews",
    abbreviation: "SDE-HDR",
    apiParam: { structuredDataEndpoint: "homedepotReviews" },
  },
];

// List of all product IDs (used for filter dropdowns)
export const ALL_PRODUCT_IDS: string[] = PRODUCTS.map((p) => p.id);

// Map product ID to API params for backend calls
export const PRODUCT_TYPE_MAP: Record<
  string,
  { sourceProduct?: string; structuredDataEndpoint?: string }
> = Object.fromEntries(
  PRODUCTS.filter((p) => p.apiParam).map((p) => [p.id, p.apiParam!])
);

// Map product ID to display name
export const PRODUCT_DISPLAY_NAMES: Record<string, string> = Object.fromEntries(
  PRODUCTS.map((p) => [p.id, p.displayName])
);

// Map various identifiers to abbreviations (for badges)
export const PRODUCT_ABBREVIATIONS: Record<string, string> = Object.fromEntries(
  PRODUCTS.flatMap((p) => [
    [p.displayName, p.abbreviation], // "API" -> "api"
    [p.id, p.abbreviation], // "api" -> "api"
    [p.abbreviation, p.abbreviation], // "api" -> "api" (identity)
  ])
);

// Map abbreviations and IDs to full display names
export const PRODUCT_FULL_NAMES: Record<string, string> = Object.fromEntries(
  PRODUCTS.flatMap((p) => [
    [p.abbreviation, p.displayName], // "api" -> "API"
    [p.id, p.displayName], // "api" -> "API"
  ])
);

// Parameter mappings
export const SCRAPE_OPTIONS_MAP: Record<string, string> = {
  UltraPremium: "ultraPremium",
  Premium: "premium",
  Render: "render",
};

// UI-facing list of known scrape parameters
export const KNOWN_PARAMETERS = Object.keys(SCRAPE_OPTIONS_MAP);

// Shared debounce for analytics filter API calls
export const ANALYTICS_FILTER_API_DEBOUNCE_MS = 300;

export const MAX_DOMAINS_SELECTION = 10;

// Chart colors
export const CHART_COLORS = {
  SUCCESS: "#5359eb",
  FAILURE: "#ea4140",
  CONCURRENT_THREADS: "#909090",
} as const;

// Badge colors
export const PRODUCT_BADGE_COLORS = [
  "bg-purple-100 text-purple-700",
  "bg-pink-100 text-pink-700",
  "bg-yellow-100 text-yellow-700",
  "bg-green-100 text-green-700",
];

// Severity styles
export const SEVERITY_STYLES = {
  low: "bg-green-100 text-green-800",
  medium: "bg-orange-100 text-orange-800",
  high: "bg-red-100 text-red-800",
  default: "bg-gray-100 text-gray-800",
};

// Default pagination sizes
export const DOMAIN_PAGE_SIZE = 25;
export const ERROR_LOGS_PAGE_SIZE = 25;
export const DEBOUNCE_DELAY = 500;

// Default selected columns for tables
export const DEFAULT_SELECTED_COLUMNS: SelectedErrorColumns = {
  domain: [
    "domain",
    "requests",
    "successRate",
    "cancelledRequests",
    "credits",
    "ultraPremium",
    "premium",
    "render",
    "aiParser",
  ],
  error: [
    "request_id",
    "timestamp",
    "severity",
    "url",
    "status_code",
    "retries",
  ],
};